import { useMemo } from "react";
import type {
  WorkflowUIConfig,
  WorkflowUITile,
  WorkflowUITileAction,
  WorkflowUIStep
} from "./types";
import type { PathContext } from "./utils";
import { resolveBindingValue, translateText } from "./utils";
import "./WorkflowRenderer.css";

export interface WorkflowRendererProps {
  ui: WorkflowUIConfig;
  stepId: string;
  language: string;
  fallbackLanguage?: string;
  context: PathContext;
  validationErrors?: Record<string, string>;
  onBindingChange: (tile: WorkflowUITile, binding: string | undefined, value: unknown) => void;
  onAction: (action: WorkflowUITileAction, tile: WorkflowUITile) => void | Promise<void>;
  disabled?: boolean;
}

export function WorkflowRenderer(props: WorkflowRendererProps) {
  const {
    ui,
    stepId,
    language,
    fallbackLanguage = "en-US",
    context,
    validationErrors = {},
    onBindingChange,
    onAction,
    disabled = false
  } = props;

  const step = useMemo(() => getStepById(ui, stepId), [ui, stepId]);

  if (!step) {
    return (
      <div className="workflow-empty">
        <p>Kein Schritt für dieses UI vorhanden.</p>
      </div>
    );
  }

  return (
    <div className="workflow-step">
      {step.tiles.map((tile) => (
        <TileRenderer
          key={tile.id}
          tile={tile}
          step={step}
          language={language}
          fallbackLanguage={fallbackLanguage}
          context={context}
          error={validationErrors[tile.id]}
          onBindingChange={onBindingChange}
          onAction={onAction}
          disabled={disabled}
        />
      ))}
    </div>
  );
}

interface TileRendererProps {
  tile: WorkflowUITile;
  step: WorkflowUIStep;
  language: string;
  fallbackLanguage: string;
  context: PathContext;
  error?: string;
  onBindingChange: (tile: WorkflowUITile, binding: string | undefined, value: unknown) => void;
  onAction: (action: WorkflowUITileAction, tile: WorkflowUITile) => void | Promise<void>;
  disabled: boolean;
}

function TileRenderer(props: TileRendererProps) {
  const { tile, language, fallbackLanguage, context, error, onBindingChange, onAction, disabled } = props;
  const translatedTitle = translateText(tile.title, language, fallbackLanguage);
  const translatedText = translateText(tile.text, language, fallbackLanguage);

  switch (tile.componentType) {
    case "Hero":
      return (
        <section className="workflow-section hero-section">
          {translatedTitle ? <h2>{translatedTitle}</h2> : null}
          {translatedText ? <p>{translatedText}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "ChoiceGrid":
      return (
        <section className="workflow-section choice-section">
          {translatedTitle ? <h3>{translatedTitle}</h3> : null}
          {translatedText ? <p>{translatedText}</p> : null}
          <div className="choice-grid-options">
            {(tile.props?.choices ?? []).map((choice) => {
              const currentValue = resolveBindingValue(tile.bindings?.value, context);
              const isActive = currentValue === choice.key;
              const flagClassName =
                typeof choice.flagCode === "string" && choice.flagCode.length > 0
                  ? `fi fi-${choice.flagCode.toLowerCase()}`
                  : undefined;

              const handleSelect = async () => {
                if (disabled) {
                  return;
                }
                onBindingChange(tile, tile.bindings?.value, choice.key);
                for (const action of tile.actions ?? []) {
                  await onAction(action, tile);
                }
              };

              return (
                <button
                  key={choice.key}
                  type="button"
                  className={isActive ? "choice active" : "choice"}
                  disabled={disabled}
                  aria-pressed={isActive}
                  onClick={() => void handleSelect()}
                >
                  <span className="choice-content">
                    {flagClassName ? (
                      <span className="choice-flag" aria-hidden="true">
                        <span className={flagClassName} />
                      </span>
                    ) : null}
                    <span className="choice-label">{choice.label}</span>
                  </span>
                  {choice.description ? <small>{choice.description}</small> : null}
                </button>
              );
            })}
          </div>
          {error ? <p className="section-error">{error}</p> : null}
        </section>
      );
    case "TextField":
      return (
        <section className="workflow-section field-section">
          {translatedTitle ? <label className="field-label">{translatedTitle}</label> : null}
          {translatedText ? <p className="field-description">{translatedText}</p> : null}
          <input
            type="text"
            value={(resolveBindingValue(tile.bindings?.value, context) as string) ?? ""}
            onChange={(event) => onBindingChange(tile, tile.bindings?.value, event.target.value)}
            disabled={disabled}
          />
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "PasswordField":
      return (
        <section className="workflow-section field-section">
          {translatedTitle ? <label className="field-label">{translatedTitle}</label> : null}
          {translatedText ? <p className="field-description">{translatedText}</p> : null}
          <input
            type="password"
            value={(resolveBindingValue(tile.bindings?.value, context) as string) ?? ""}
            onChange={(event) => onBindingChange(tile, tile.bindings?.value, event.target.value)}
            disabled={disabled}
            autoComplete="new-password"
          />
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "NumberField":
      return (
        <section className="workflow-section field-section">
          {translatedTitle ? <label className="field-label">{translatedTitle}</label> : null}
          {translatedText ? <p className="field-description">{translatedText}</p> : null}
          <input
            type="number"
            value={(resolveBindingValue(tile.bindings?.value, context) as number) ?? ""}
            onChange={(event) => onBindingChange(tile, tile.bindings?.value, event.target.valueAsNumber)}
            disabled={disabled}
          />
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "TextArea":
      return (
        <section className="workflow-section field-section">
          {translatedTitle ? <label className="field-label">{translatedTitle}</label> : null}
          {translatedText ? <p className="field-description">{translatedText}</p> : null}
          <textarea
            value={(resolveBindingValue(tile.bindings?.value, context) as string) ?? ""}
            onChange={(event) => onBindingChange(tile, tile.bindings?.value, event.target.value)}
            disabled={disabled}
            rows={4}
          />
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "DateTimeField":
      return (
        <section className="workflow-section field-section">
          {translatedTitle ? <label className="field-label">{translatedTitle}</label> : null}
          {translatedText ? <p className="field-description">{translatedText}</p> : null}
          <input
            type="datetime-local"
            value={(resolveBindingValue(tile.bindings?.value, context) as string) ?? ""}
            onChange={(event) => onBindingChange(tile, tile.bindings?.value, event.target.value)}
            disabled={disabled}
          />
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "Checkbox":
      return (
        <section className="workflow-section field-section checkbox-section">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={(resolveBindingValue(tile.bindings?.value, context) as boolean) ?? false}
              onChange={(event) => onBindingChange(tile, tile.bindings?.value, event.target.checked)}
              disabled={disabled}
            />
            {translatedTitle ? <span>{translatedTitle}</span> : null}
          </label>
          {translatedText ? <p className="field-description">{translatedText}</p> : null}
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "Select":
      return (
        <section className="workflow-section field-section">
          {translatedTitle ? <label className="field-label">{translatedTitle}</label> : null}
          {translatedText ? <p className="field-description">{translatedText}</p> : null}
          <select
            value={(resolveBindingValue(tile.bindings?.value, context) as string) ?? ""}
            onChange={(event) => onBindingChange(tile, tile.bindings?.value, event.target.value)}
            disabled={disabled}
          >
            <option value="">-- Bitte wählen --</option>
            {(Array.isArray((tile.props as any)?.options) ? (tile.props as any).options : []).map((option: any) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    case "ActionButton":
      return (
        <section className="workflow-section action-section">
          {tile.actions?.map((action: any) => (
            <button
              key={action.id}
              className={`workflow-button ${action.style || 'default'}`}
              onClick={() => onAction(action, tile)}
              disabled={disabled}
            >
              {translateText(action.label, language, fallbackLanguage) || translatedTitle || 'Submit'}
            </button>
          ))}
        </section>
      );
    case "Summary": {
      const data = resolveBindingValue(tile.bindings?.data, context);
      return (
        <section className="workflow-section summary-section">
          {translatedTitle ? <h3>{translatedTitle}</h3> : null}
          <pre>{JSON.stringify(data ?? {}, null, 2)}</pre>
          {error ? <p className="section-error">{error}</p> : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    }
    case "Markdown":
      return (
        <section className="workflow-section markdown-section">
          {translatedTitle ? <h3>{translatedTitle}</h3> : null}
          {translatedText ? (
            <div
              className="markdown"
              dangerouslySetInnerHTML={{ __html: convertMarkdownToHtml(translatedText) }}
            />
          ) : null}
          {renderActions(tile, language, disabled, onAction)}
        </section>
      );
    default:
      return (
        <section className="workflow-section unsupported-section">
          <p>Komponente "{tile.componentType}" wird noch nicht unterstützt.</p>
        </section>
      );
  }
}

function renderActions(
  tile: WorkflowUITile,
  language: string,
  disabled: boolean,
  onAction: (action: WorkflowUITileAction, tile: WorkflowUITile) => void | Promise<void>
) {
  const actions = tile.actions ?? [];
  if (actions.length === 0) {
    return null;
  }

  const handle = (action: WorkflowUITileAction) => {
    if (disabled) {
      return;
    }
    void Promise.resolve(onAction(action, tile));
  };

  return (
    <div className="section-actions">
      {actions.map((action, index) => (
        <button
          key={`${tile.id}-action-${index}`}
          type="button"
          className={`section-action-btn action-${action.type}`}
          disabled={disabled}
          onClick={() => handle(action)}
        >
          {resolveActionLabel(action, language)}
        </button>
      ))}
    </div>
  );
}

function resolveActionLabel(action: WorkflowUITileAction, language: string): string {
  if (typeof action.message === "string" && action.message.trim().length > 0) {
    return action.message;
  }

  if (action.message && typeof action.message === "object") {
    const map = action.message as Record<string, string>;
    const localized = map[language] ?? map["en-US"];
    if (localized) {
      return localized;
    }
  }

  return getActionLabelFallback(action.type, language);
}

function getActionLabelFallback(actionType: WorkflowUITileAction["type"], language: string): string {
  const labels: Record<string, Record<WorkflowUITileAction["type"], string>> = {
    "de-DE": {
      navigate: "Weiter",
      submit: "Absenden",
      finish: "Fertig",
      message: "Nachricht"
    },
    "en-US": {
      navigate: "Continue",
      submit: "Submit",
      finish: "Done",
      message: "Message"
    }
  };

  const localized = labels[language]?.[actionType];
  if (localized) {
    return localized;
  }

  const fallback = labels["en-US"][actionType];
  return fallback ?? actionType;
}

function convertMarkdownToHtml(content: string): string {
  return content
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br />");
}

function getStepById(ui: WorkflowUIConfig, stepId: string): WorkflowUIStep | undefined {
  return ui.steps.find((candidate) => candidate.id === stepId);
}
